import { generateHTML } from '@tiptap/html';
import { EditorContent } from '@tiptap/react';
import debounce from 'lodash/debounce';
import { useRef, useState, useEffect } from 'react';

import {
  useArticleTiptapEditor,
  getExtensions,
  type ArticleType,
} from '@xing-com/crate-articles-article-editor';
import { type Maybe } from '@xing-com/crate-common-graphql-types';

import { parseMembership } from '../../helpers';
import type { ArticleAuthorType } from '../../types';
import ArticlePaywall from '../article-paywall';
import { Styled } from './styles';

const getElementToPosition = (children?: HTMLCollection): Element | null => {
  return children && children.length < 3
    ? children[0]
    : children
      ? children[1]
      : null;
};

type ArticleViewerContentProps = {
  article: Maybe<ArticleType>;
  author?: Maybe<ArticleAuthorType>;
  showPaywall?: boolean;
};

const ServerSideRenderedContent: React.FC<ArticleViewerContentProps> = ({
  article,
}) => {
  const htmlContent = generateHTML(article?.content || {}, getExtensions());

  return (
    <div className="tiptap" dangerouslySetInnerHTML={{ __html: htmlContent }} />
  );
};

const ArticleViewerContent: React.FC<ArticleViewerContentProps> = ({
  article,
  author,
  showPaywall = true,
}) => {
  const [paywallPosition, setPaywallPosition] = useState<number>(0);

  const editor = useArticleTiptapEditor(false);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  const membership = parseMembership(article?.settings?.membership);

  useEffect(() => {
    const updatePaywallPosition = (): void => {
      if (contentContainerRef.current) {
        let elementToPositionPaywall;

        if (editor) {
          const containerChildren = contentContainerRef.current.querySelector(
            "[data-qa='article-editor']"
          )?.children;

          elementToPositionPaywall = getElementToPosition(containerChildren);
        } else {
          const containerChildren =
            contentContainerRef.current.querySelector('.tiptap')?.children;

          elementToPositionPaywall = getElementToPosition(containerChildren);
        }

        const containerTopPosition =
          contentContainerRef.current.getBoundingClientRect().top;
        const topPosition = !elementToPositionPaywall?.getBoundingClientRect()
          .top
          ? 0
          : elementToPositionPaywall?.getBoundingClientRect().top -
            containerTopPosition;

        setPaywallPosition(topPosition);
      }
    };

    const debouncedUpdatePaywallPosition = debounce(updatePaywallPosition, 300);

    window.addEventListener('resize', debouncedUpdatePaywallPosition);
    updatePaywallPosition();

    return () => {
      window.removeEventListener('resize', debouncedUpdatePaywallPosition);
      debouncedUpdatePaywallPosition.cancel();
    };
  }, [contentContainerRef, editor]);

  return (
    <Styled.ArticleContentContainer data-qa="article-viewer-content">
      {article?.headline && membership && (
        <Styled.MembershipFlag
          data-qa="article-viewer-membership-flag"
          variant={membership}
        />
      )}
      {article?.headline ? (
        <Styled.ArticleHeadline>{article.headline}</Styled.ArticleHeadline>
      ) : null}

      {article?.subtitle ? (
        <Styled.ArticleSubtitleCopy>
          {article.subtitle}
        </Styled.ArticleSubtitleCopy>
      ) : null}

      {article?.content ? (
        <Styled.ContentContainer
          ref={contentContainerRef}
          data-qa="article-viewer-content-editor"
          data-top="top"
          hasPaywall={showPaywall && !!membership}
        >
          {editor ? (
            <EditorContent editor={editor} />
          ) : (
            <ServerSideRenderedContent article={article} />
          )}
          {article?.content && showPaywall && membership && (
            <ArticlePaywall
              membership={membership}
              articleTitle={article?.headline || ''}
              author={author}
              topOffset={paywallPosition}
            />
          )}
        </Styled.ContentContainer>
      ) : null}
    </Styled.ArticleContentContainer>
  );
};

export default ArticleViewerContent;
